// El parseo/validación de RUTs se movió a `utils/rutList` para poder
// reutilizarlo desde otras tablas con filtro de RUT. Se re-exporta acá para no
// romper los imports existentes de este módulo.
export type { IRutCandidate } from "../../../utils/rutList";
export { parseRutCandidate, parseRutList } from "../../../utils/rutList";

export const DEFAULT_PAGE_SIZE = 10;
export const MIN_PAGE_SIZE = 1;
export const MAX_PAGE_SIZE = 1000;

export const parsePageSize = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed < MIN_PAGE_SIZE || parsed > MAX_PAGE_SIZE) {
    return DEFAULT_PAGE_SIZE;
  }
  return parsed;
};

const DEFAULT_API_ERRORS: Record<number, string> = {
  400: "La solicitud no es válida. Revisa los datos ingresados.",
  401: "Tu sesión expiró o no es válida. Vuelve a iniciar sesión.",
  403: "No tienes permisos para realizar esta acción.",
  404: "No se encontró la información solicitada.",
  409: "La solicitud no pudo completarse porque existe un conflicto con los datos actuales.",
  422: "Algunos datos no son válidos. Revísalos e intenta nuevamente.",
  429: "Se realizaron demasiadas solicitudes. Espera un momento e intenta nuevamente.",
};

const findMessage = (payload: unknown): string | undefined => {
  if (typeof payload === "string") return payload.trim() || undefined;
  if (!payload || typeof payload !== "object") return undefined;

  const record = payload as Record<string, unknown>;
  for (const key of ["message", "mensaje", "detail", "error"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      const nested = findMessage(value);
      if (nested) return nested;
    }
  }

  if (Array.isArray(record.errors)) {
    for (const error of record.errors) {
      const nested = findMessage(error);
      if (nested) return nested;
    }
  }

  return undefined;
};

export const getDefaultApiError = (status: number): string => {
  if (DEFAULT_API_ERRORS[status]) return DEFAULT_API_ERRORS[status];
  if (status >= 500) return "Ocurrió un error en el servidor. Intenta nuevamente más tarde.";
  return "No se pudo completar la solicitud. Intenta nuevamente.";
};

export const extractApiErrorMessage = (payload: unknown, status: number): string => {
  const explicitlySafe = Boolean(payload && typeof payload === "object" && (payload as Record<string, unknown>).userSafe === true);
  const message = findMessage(payload);
  if (status >= 500 && !explicitlySafe) return getDefaultApiError(status);
  if (message && !/^(?:http|error)\s*\d+\b|unexpected error|internal server error/i.test(message)) return message;
  return getDefaultApiError(status);
};

export const readApiErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get("content-type") ?? "";
  try {
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
    return extractApiErrorMessage(payload, response.status);
  } catch {
    return getDefaultApiError(response.status);
  }
};

export const getUserErrorMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof Error)) return fallback;
  if (error instanceof TypeError || /failed to fetch|networkerror|network request/i.test(error.message)) {
    return "No se pudo conectar con el servidor. Revisa tu conexión e intenta nuevamente.";
  }
  const message = error.message.trim();
  if (/^(?:http|error)\s*\d+\b/i.test(message)) return fallback;
  return message || fallback;
};

interface IApplicationProcessContext {
  computedStatus?: string | null;
  businessArea?: string | null;
  vacancy?: string | null;
  recruiter?: string | null;
  activeProcessBusinessAreaCode?: string | null;
  activeProcessId?: string | null;
  activeProcessRecruiter?: string | null;
}

/** Observación por defecto al validar manualmente sin modificar datos. */
export const OBSERVACION_SIN_CAMBIOS = "Aprobado sin cambios.";

/**
 * Observación de la validación manual mientras se edita. La parte que genera
 * el sistema (OBSERVACION_SIN_CAMBIOS o el resumen de cambios) se mantiene al
 * día con los datos:
 * - Si la persona no tocó el texto, es exactamente lo generado.
 * - Si lo tocó pero conserva lo generado antes (p. ej. agregó algo al final),
 *   solo esa parte se reemplaza por lo nuevo.
 * - Si lo reescribió, se respeta su texto.
 */
export const sincronizarObservacion = ({
  actual,
  generadaAnterior,
  generadaNueva,
  editadaPorUsuario,
}: {
  actual: string;
  generadaAnterior: string;
  generadaNueva: string;
  editadaPorUsuario: boolean;
}): string => {
  if (!editadaPorUsuario) return generadaNueva;
  if (generadaAnterior && actual.includes(generadaAnterior)) {
    return actual.replace(generadaAnterior, generadaNueva);
  }
  return actual;
};

export const resolveDisplayedProcessContext = (item: IApplicationProcessContext) => {
  const isInActiveProcess = item.computedStatus === "PROCESO_ACTIVO";

  return {
    businessArea: isInActiveProcess
      ? (item.activeProcessBusinessAreaCode || item.businessArea || "")
      : (item.businessArea || ""),
    processId: isInActiveProcess
      ? (item.activeProcessId || item.vacancy || "")
      : (item.vacancy || ""),
    recruiter: isInActiveProcess
      ? (item.activeProcessRecruiter || item.recruiter || "")
      : (item.recruiter || ""),
  };
};
